"use client"

import {
  useState,
  useEffect,
  useRef,
  useCallback
} from "react";
import {
  FileText,
  Download,
  Eye,
  Upload,
  ImageIcon,
  DollarSign,
  CheckCircle,
  Loader2,
  Building2,
  UserCircle,
  Plus,
  Trash2,
  Lock,
  Truck
} from "lucide-react";
import { Switch } from "antd";
import type { ContainerTask } from "../../../types/container";
import { driverPaymentAPI } from "../../../services/api";
import { taskAPI } from "../../../services/taskApi";
import api from "../../../services/api";
import { timelineApi } from "../../../services/timelineApi";
import type { TaskPhoto } from "../../../types/timeline";
import { orderFileApi, type OrderFile } from "../../../services/orderFileApi";
import { SalaryPanel } from "./SalaryPanel";
import { DispatchFilesSection } from "./DispatchFilesSection";

const BACKEND_URL = (import.meta.env.VITE_API_URL || 'http://localhost:4000/api').replace(/\/api$/, '');

/** Resolve a file URL to a full backend URL if it's a relative path */
const resolveFileUrl = (url: string) => {
  if (url.startsWith('http')) return url;
  return `${BACKEND_URL}${url}`;
};

interface PaymentInfo {
  routePrice: number
  taskType: string
  waitingHourlyRate: number
  waitingDuration: number
  extraPay: number
}

// Draft/confirmed payment from new salary system

interface DraftPayment {
  id: number
  payment_number: string
  task_id: number
  driver_id: number | null
  driver_name: string | null
  driver_type: string | null
  payment_role: 'driver' | 'truck_owner'
  recipient_driver_id: number | null
  base_rate: number
  extra_rate: number
  total_amount: number
  salary_rate_id: number | null
  status: string
  extras: DraftPaymentExtra[]
}

// Backend driver payment data (legacy)
interface BackendDriverPayment {
  id: number
  paymentNumber: string
  driverName: string
  baseRate: number
  extraRate: number
  totalAmount: number
  status: string
  taskType: string
  containerNumber: string
}

// Task file record from task_files table
interface TaskFileRecord {
  id: number
  file_type: string
  file_url: string
  file_name: string
  file_size?: number
  category: string
  created_at: string
}

// Fixed document type slots (always visible)
const DOCUMENT_SLOTS: { type: string; label: string }[] = [
  { type: 'bol', label: 'BOL' },
  { type: 'do', label: 'DO' },
  { type: 'paper', label: 'Paper' },
]

// Fixed image category slots (always visible, matching original design)
const IMAGE_CATEGORY_SLOTS: { category: string; name: string; label: string }[] = [
  { category: 'port_ticket', name: 'Out of Port', label: 'Container #' },
  { category: 'on_stop', name: 'On Stop', label: 'Lot #' },
  { category: 'wh_gate', name: 'Arrived HW', label: 'Container # + Gate' },
  { category: 'entering_gate', name: 'Entering Gate', label: '' },
  { category: 'seal', name: 'Seal', label: '' },
  { category: 'open_door', name: 'Opened Door', label: '' },
  { category: 'park_container', name: 'Container on Dock', label: 'Container # + Chassis + Lot #' },
  { category: 'phone_call', name: 'Phone Call Screenshot', label: '' },
  { category: 'empty', name: 'Empty', label: '' },
  { category: 'leaving_gate', name: 'Leaving Gate', label: '' },
  { category: 'paperwork', name: 'DO', label: '' },
  { category: 'container', name: 'Ticket', label: 'Receipt/Invoice' },
  { category: 'other', name: 'Other', label: 'Additional Photos' },
]

interface PaymentDocumentsTabProps {
  container: ContainerTask
  paymentInfo: PaymentInfo
  documents?: Document[]
  onContainerChange: (container: ContainerTask) => void
  onImageClick: (imageUrl: string) => void
}

export function PaymentDocumentsTab({
  container,
  paymentInfo,
  onImageClick,
}: PaymentDocumentsTabProps) {

  // Document and photo states
  const [orderFiles, setOrderFiles] = useState<OrderFile[]>([])
  const [taskPhotos, setTaskPhotos] = useState<TaskPhoto[]>([])
  const [loadingFiles, setLoadingFiles] = useState(true)

  // Fetch order-level documents and task photos
  useEffect(() => {
    const fetchFilesAndPhotos = async () => {
      const taskDbId = container.id ? parseInt(container.id) : undefined
      const orderId = container.orderId

      setLoadingFiles(true)
      try {
        const promises: Promise<unknown>[] = []

        // Fetch order-level documents (BOL, DO, Paperwork) if orderId available
        if (orderId) {
          promises.push(orderFileApi.getFiles(orderId))
        } else {
          promises.push(Promise.resolve([]))
        }

        // Fetch task photos
        if (taskDbId) {
          promises.push(timelineApi.getPhotos(taskDbId))
        } else {
          promises.push(Promise.resolve([]))
        }

        const [filesResult, photosResult] = await Promise.allSettled(promises)

        if (filesResult.status === 'fulfilled') {
          setOrderFiles(Array.isArray(filesResult.value) ? filesResult.value as OrderFile[] : [])
        }

        if (photosResult.status === 'fulfilled') {
          setTaskPhotos(Array.isArray(photosResult.value) ? photosResult.value as TaskPhoto[] : [])
        }
      } catch (err) {
        console.error('Error fetching files/photos:', err)
      } finally {
        setLoadingFiles(false)
      }
    }

    fetchFilesAndPhotos()
  }, [container.id, container.orderId])


  // Style classes matching OrderDispatchPage
  const fieldContainerClass = "bg-gray-50 rounded-xl p-4"
  const inputClass = "w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
  const labelClass = "text-xs font-medium text-gray-500 uppercase block mb-2"
  const buttonClass = "px-4 py-2 text-sm font-medium rounded-lg transition-all"


  // Group photos by category
  const photosByCategory: Record<string, TaskPhoto[]> = {}
  for (const photo of taskPhotos) {
    const cat = photo.photo_type || photo.photo_category || 'other'
    if (!photosByCategory[cat]) photosByCategory[cat] = []
    photosByCategory[cat].push(photo)
  }

  // Format relative time
  const formatRelativeTime = (dateStr: string): string => {
    const date = new Date(dateStr)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    if (diffMins < 60) return `${diffMins}m ago`
    const diffHours = Math.floor(diffMins / 60)
    if (diffHours < 24) return `${diffHours}h ago`
    const diffDays = Math.floor(diffHours / 24)
    return `${diffDays}d ago`
  }

  return (
    <div className="grid grid-cols-2 gap-x-6 gap-y-4">
      {/* Row 0: Section headers */}
      <h5 className="text-sm font-semibold text-gray-900 uppercase tracking-wide flex items-center gap-2">
        <div className="w-1 h-4 bg-blue-500 rounded-full"></div>
        Documents & Images
      </h5>
      <h5 className="text-sm font-semibold text-gray-900 uppercase tracking-wide flex items-center gap-2">
        <div className="w-1 h-4 bg-green-500 rounded-full"></div>
        Driver Salary
      </h5>

      {/* Row 1: Paperwork (read-only from Order) | Payment Info */}
      <div className={`${fieldContainerClass}`}>
        <div className="flex items-center gap-2 mb-4">
          <FileText className="h-5 w-5 text-gray-600" />
          <label className={labelClass}>Paperwork</label>
          <span className="text-xs text-gray-400 font-normal normal-case">(from Order)</span>
        </div>
        <div className="space-y-4">
          {loadingFiles ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
              <span className="ml-2 text-sm text-gray-500">Loading documents...</span>
            </div>
          ) : (
            DOCUMENT_SLOTS.map((slot) => {
              // Match order-level files for this document type
              // Map 'paper' slot type to 'paperwork' file_type in order_files
              const orderFileType = slot.type === 'paper' ? 'paperwork' : slot.type
              const matchedFiles = orderFiles.filter(
                (f) => f.file_type === orderFileType
              )

              if (matchedFiles.length > 0) {
                return (
                  <div key={slot.type} className="space-y-2">
                    {matchedFiles.map((matchedFile) => (
                      <div key={matchedFile.id} className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-16 bg-red-100 rounded-lg flex items-center justify-center">
                            <FileText className="h-6 w-6 text-red-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{slot.label}</p>
                            <p className="text-sm text-gray-500">{matchedFile.file_name || `${slot.label}_document`}</p>
                            <p className="text-xs text-gray-400">Uploaded {formatRelativeTime(matchedFile.created_at)}</p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            className={`${buttonClass} bg-gray-100 hover:bg-gray-200 text-gray-700`}
                            onClick={() => window.open(resolveFileUrl(matchedFile.file_url), '_blank')}
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <a
                            href={resolveFileUrl(matchedFile.file_url)}
                            download={matchedFile.file_name || undefined}
                            className={`${buttonClass} bg-gray-100 hover:bg-gray-200 text-gray-700 inline-flex items-center`}
                          >
                            <Download className="h-4 w-4" />
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                )
              }

              return (
                <div key={slot.type} className="flex items-center justify-between p-3 bg-gray-50 border-2 border-dashed border-gray-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-16 bg-gray-100 rounded-lg flex items-center justify-center">
                      <Upload className="h-6 w-6 text-gray-400" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-600">{slot.label}</p>
                      <p className="text-xs text-gray-400">Not uploaded — manage in Order Documents</p>
                    </div>
                  </div>
                </div>
              )
            })
          )}
        </div>
      </div>

      <div className="row-span-2">
        {(() => {
          const taskDbId = container.id ? parseInt(container.id) : 0;
          return taskDbId > 0 ? (
            <SalaryPanel taskDbId={taskDbId} hasDriver={!!container.driver} refreshKey={Number(container.isOverweight || 0) + String((container as Record<string, unknown>).paymentAdjustedAt || '').length} />
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
              <p className="text-sm text-gray-400">No salary data</p>
            </div>
          );
        })()}
      </div>

      {/* Row 2: Driver Images */}
      <div className={`${fieldContainerClass}`}>
        <div className="flex items-center gap-2 mb-4">
          <ImageIcon className="h-5 w-5 text-gray-600" />
          <label className={labelClass}>Driver Images</label>
        </div>
        <div className="space-y-4">
          {loadingFiles ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
              <span className="ml-2 text-sm text-gray-500">Loading photos...</span>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 max-h-96 overflow-y-auto">
              {IMAGE_CATEGORY_SLOTS.map((slot) => {
                const photos = photosByCategory[slot.category] || []
                return (
                  <div key={slot.category} className="bg-white border border-gray-200 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <h4 className="font-medium text-sm text-gray-900">{slot.name}</h4>
                        {slot.label && (
                          <p className="text-xs text-gray-500">{slot.label}</p>
                        )}
                      </div>
                      {photos.length > 0 ? (
                        <span className="px-2 py-1 bg-blue-50 text-blue-600 rounded text-xs font-medium">
                          {photos.length} photo{photos.length !== 1 ? 's' : ''}
                        </span>
                      ) : (
                        <span className="px-2 py-1 bg-gray-100 text-gray-400 rounded text-xs font-medium">
                          No photos
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      {photos.length > 0 ? (
                        photos.map((photo) => (
                          <div
                            key={photo.id}
                            className="relative group cursor-pointer"
                            onClick={() => onImageClick(resolveFileUrl(photo.photo_url))}
                          >
                            <img
                              src={resolveFileUrl(photo.photo_url)}
                              alt={`${slot.name} photo`}
                              className="w-full h-16 object-cover rounded-lg border border-gray-200 hover:border-blue-500 transition-colors"
                            />
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 rounded-lg transition-colors flex items-center justify-center">
                              <Eye className="h-3 w-3 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="w-full h-16 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center text-gray-400">
                          <Upload className="h-4 w-4" />
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>

      {/* Row 3: Dispatch Files (full-width) — files the dispatcher shares with the driver */}
      <DispatchFilesSection taskId={container.id ? parseInt(container.id) : null} />

    </div>
  )
}
